import type {
	SRT_Row,
	SRT_RowSelectionState,
	SRT_SortingState,
} from '@/shared/components/master-table'
import type {
	ApplyAction,
	OptimizationGroupModel,
	SettingsTemplateModel,
} from '@/shared/generated'
import type { TFunction } from 'i18next'

import React, { useCallback, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { sortingFns } from '@tanstack/react-table'
import { all, any, keyBy, prop } from 'lodash/fp'
import { CheckIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
	getOptimizationsMap,
	getOptStatus,
	getStatusBadgeVariant,
	RevokeOptimizationAction,
	useOptimizationsQuery,
} from '@/entities/optimization'
import { useOptimizationGroupsQuery } from '@/entities/optimization-group'
import { useScenariosQuery } from '@/entities/scenario'
import { useApplySettingsTemplateMutation } from '@/entities/settings-template'

import {
	createSRTColumnHelper,
	MasterTable,
	useMasterReactTable,
} from '@/shared/components/master-table'
import { SRT_ExpandAllButton } from '@/shared/components/master-table/components/buttons/SRT_ExpandAllButton'
import { SRT_TableToolbar } from '@/shared/components/master-table/components/tools/SRT_TableToolbar'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Blanket } from '@/shared/components/ui/blanket'
import { Button } from '@/shared/components/ui/button'
import { confirm } from '@/shared/components/ui/confirmer'
import {
	Item,
	ItemActions,
	ItemContent,
	ItemTitle,
} from '@/shared/components/ui/item'
import { Loader } from '@/shared/components/ui/loader'
import {
	ModalBody,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Tag } from '@/shared/components/ui/tag'
import { OptimizationStatus } from '@/shared/generated'
import { getMasterTableLocale } from '@/shared/i18n'

export function ApplyToScenarios({
	template,
	close,
	action,
}: {
	template: SettingsTemplateModel
	close: () => void
	action: ApplyAction
}) {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const [rowSelection, setRowSelection] = React.useState<SRT_RowSelectionState>(
		{}
	)
	const optimizationGroupsQuery = useOptimizationGroupsQuery()
	const scenarionsQuery = useScenariosQuery()
	const optimizationsQuery = useOptimizationsQuery({
		select: getOptimizationsMap,
	})
	const applyMutation = useApplySettingsTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['optimizations'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-overview'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaign'],
			})
			queryClient.invalidateQueries({
				queryKey: ['scenarios'],
			})
			queryClient.invalidateQueries({
				queryKey: ['scenarios-query-list'],
			})
		},
	})
	const isLoading = any(prop('isLoading'), [
		scenarionsQuery,
		optimizationGroupsQuery,
		optimizationsQuery,
	])
	const data = useMemo(() => {
		const optimizationGroups = keyBy('id', optimizationGroupsQuery.data)
		const data = []
		for (const scenario of scenarionsQuery.data || []) {
			const optimizationGroup =
				optimizationGroups[scenario.optimization_group_id]
			data.push({
				id: scenario.id,
				name: scenario.name,
				optimization_group: optimizationGroup,
				is_main: scenario.is_main,
				status: getOptStatus(
					optimizationsQuery.data?.[scenario.optimization_group_id]?.status
				),
			})
		}

		return data
	}, [optimizationsQuery, optimizationGroupsQuery, scenarionsQuery.data])
	const allSelectedItemsAreValid = useMemo(
		() =>
			all(
				({ status, is_main }) =>
					(is_main &&
						[OptimizationStatus.New, OptimizationStatus.Pending].includes(
							status!
						)) ||
					!is_main,
				data.filter((row) => rowSelection[row.id])
			),
		[rowSelection, data]
	)
	const canGoNext = useMemo(
		() => Object.keys(rowSelection).length > 0 && allSelectedItemsAreValid,
		[allSelectedItemsAreValid, rowSelection]
	)
	const handleApply = useCallback(async () => {
		const answer = await confirm({
			title: t('template.apply.confirm.title', {
				defaultValue: 'Apply settings template',
			}),
			description: t('template.apply.confirm.text', {
				defaultValue: 'Are you sure you want to apply the template',
			}),
			actionText: t('app.apply', { defaultValue: 'Apply' }),
			cancelText: t('general_cancel'),
		})
		if (answer) {
			await applyMutation.mutateAsync({
				id: template.id,
				data: { ids: Object.keys(rowSelection), action },
			})
			toast.success(t('toast.entity.applied', { entity: t('entity.template') }))
			close()
		}
	}, [rowSelection, action, t, applyMutation, close, template.id])
	const table = useScenariosTable({
		data,
		rowSelection,
		setRowSelection,
	})

	return (
		<>
			<Blanket
				isTinted={isLoading || applyMutation.isPending}
				className='absolute flex items-center justify-center rounded-lg bg-white/30 backdrop-blur-xs dark:bg-black/30'
			>
				<Loader />
			</Blanket>
			<ModalHeader>
				<ModalTitle>
					{t('template.apply.modal.title', {
						name: template.name,
						defaultValue: `Apply settings template: ${template.name}`,
					})}
				</ModalTitle>
				<ModalDescription>
					{t('template.apply.modal.subtitle', {
						defaultValue:
							'This will apply the settings from the template to the current settings. Are you sure you want to apply the template',
					})}
				</ModalDescription>
			</ModalHeader>
			<ModalBody>
				{applyMutation.isError && (
					<Alert variant='destructive' className='mb-2'>
						<AlertDescription>
							{t('template.apply.error', {
								defaultValue:
									'We could not apply the template. Please ensure that the selected items are in a valid state, or try again later.',
							})}
						</AlertDescription>
					</Alert>
				)}
				<div className='pb-2'>
					<SRT_TableToolbar table={table} />
				</div>
				<div className='border-base mb-4 flex h-96 flex-1 flex-col overflow-hidden rounded-lg border border-solid'>
					<MasterTable table={table} />
				</div>
			</ModalBody>
			<ModalFooter className='flex justify-between'>
				<Button data-testid='apply-template-modal-cancel' onClick={close}>
					{t('general_cancel')}
				</Button>
				<Button
					data-testid='apply-template-modal-apply'
					variant='primary'
					disabled={!canGoNext || applyMutation.isPending}
					onClick={handleApply}
				>
					<CheckIcon />
					{t('general_apply')}
				</Button>
			</ModalFooter>
		</>
	)
}
type OptimizationGroupDataRow = {
	id: string
	name: string
	is_main: boolean
	optimization_group: OptimizationGroupModel
	status?: OptimizationStatus
}
function useScenariosTable({
	data,
	rowSelection,
	setRowSelection,
}: {
	data: OptimizationGroupDataRow[]
	rowSelection: SRT_RowSelectionState
	setRowSelection: React.Dispatch<React.SetStateAction<SRT_RowSelectionState>>
}) {
	const { t } = useTranslation()

	const [expanded, setExpanded] = useState({})

	const [sorting, setSorting] = useState<SRT_SortingState>([
		{
			desc: false,
			id: 'name',
		},
	])

	return useMasterReactTable<OptimizationGroupDataRow>({
		localization: getMasterTableLocale(),
		data,
		columns: getColumns(t),
		state: {
			sorting,
			rowSelection,
			expanded,
			grouping: ['optimization_group.name'],
		},
		displayColumnDefOptions: {
			'srt-row-select': {
				size: 34,
			},
			'srt-row-expand': {
				Header: ({ table }) => <SRT_ExpandAllButton table={table} />,
				GroupedCell: () => null,
				enableResizing: true,
				size: 45,
			},
		},
		divideX: true,
		layoutMode: 'grid',
		enableGrouping: true,
		groupedColumnMode: 'remove',
		enableColumnFilters: true,
		enableVirtualization: true,

		getRowId: (row) => row.id,
		enableColumnActions: false,
		getRowClassName({ row }) {
			if (!row.original.status) {
				return ''
			}
			if (!row.original.is_main) {
				return ''
			}
			if (
				![OptimizationStatus.New, OptimizationStatus.Pending].includes(
					row.original.status
				)
			) {
				return 'text-muted-foreground'
			}

			return ''
		},
		enableRowSelection: (row: SRT_Row<OptimizationGroupDataRow>) => {
			if (!row.parentId) {
				return false
			}
			if (!row.original.status) {
				return false
			}
			if (!row.original.is_main) {
				return true
			}

			return (
				row.original.is_main &&
				[OptimizationStatus.New, OptimizationStatus.Pending].includes(
					row.original.status
				)
			)
		},
		enableSorting: true,
		enableColumnPinning: false,
		defaultRowHeight: 36,
		getRowHeight(row) {
			return row?.depth === 0 ? 56 : 36
		},
		renderGroup: ({ row }) => <TableOptimzationGroupGroup row={row} />,
		onRowSelectionChange: setRowSelection,
		defaultColumn: {
			minSize: 28,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		onExpandedChange: setExpanded,
		onSortingChange: setSorting,
	})
}
const columnHelper = createSRTColumnHelper<OptimizationGroupDataRow>()
const getColumns = (t: TFunction) => [
	columnHelper.accessor('name', {
		size: 250,
		meta: {
			label: t('scenario_name', { defaultValue: 'Scenario name' }),
			placeholder: t('search.scenarios_or_ogs.placeholder', {
				defaultValue: 'Search scenario or OG ...',
			}),
			variant: 'text',
		},
		filterFn: (row, columnId, filterValue) =>
			row.original.optimization_group?.name
				?.toLowerCase?.()
				.includes(filterValue.toLowerCase()) ||
			row.original.name?.toLowerCase?.().includes(filterValue.toLowerCase()),
		sortingFn: (rowA, rowB) => {
			if (rowA.depth === 0) {
				if (rowA.groupingValue === 'undefined') {
					return -1
				}

				return sortingFns.alphanumeric(rowA, rowB, 'optimization_group.name')
			}

			return sortingFns.basic(rowB, rowA, 'is_main')
		},
		enableColumnFilter: true,
		header: t('scenario_name', { defaultValue: 'Scenario name' }),
		Cell: ({ cellValue }) => (
			<div className='truncate text-sm'>{cellValue}</div>
		),
	}),

	columnHelper.accessor('optimization_group.name', {
		header: t('optimization_group', { defaultValue: 'Optimization group' }),
		Cell: ({ cellValue }) => (
			<div className='truncate text-sm'>{cellValue}</div>
		),
	}),

	columnHelper.accessor('is_main', {
		id: 'is_main',
		header: t('app.type', { defaultValue: 'Type' }),
		enableColumnFilter: true,
		meta: {
			label: t('scenario_type', { defaultValue: 'Scenario type' }),
			variant: 'select',
			options: [
				{
					label: t('is_main', { defaultValue: 'Is main' }),
					value: true,
				},
				{
					label: t('whatif', { defaultValue: 'What-if' }),
					value: false,
				},
			],
		},
		Cell: ({ cellValue }) => {
			const { t } = useTranslation()

			const value = cellValue
			if (value) {
				return (
					<Tag variant='info'>{t('is_main', { defaultValue: 'Is main' })}</Tag>
				)
			}

			return <Tag>{t('whatif', { defaultValue: 'What-if' })}</Tag>
		},
	}),
]

function TableOptimzationGroupGroup({
	row,
}: {
	row: SRT_Row<OptimizationGroupDataRow>
}) {
	const { t } = useTranslation()

	const name =
		row.original.optimization_group?.name ||
		t('global', { defaultValue: 'Global' })
	const status = row.original.status

	return (
		<Item size='sm' className='flex items-center pl-0'>
			<ItemActions>
				<Button
					variant='ghost'
					size='icon-sm'
					onClick={row.getToggleExpandedHandler()}
				>
					{row.getIsExpanded() ? (
						<ChevronDownIcon className='opacity-50' />
					) : (
						<ChevronRightIcon className='opacity-50' />
					)}
				</Button>
			</ItemActions>
			<ItemContent>
				<ItemTitle>{name}</ItemTitle>
			</ItemContent>
			{row.original.optimization_group && (
				<ItemActions>
					<RevokeOptimizationAction
						optimizationGroupId={row.original.optimization_group.id}
						status={row.original.status}
					/>
					<div className='min-w-25'>
						<Tag className='ml-auto' variant={getStatusBadgeVariant(status)}>
							{t(`opt_${status}`)}
						</Tag>
					</div>
				</ItemActions>
			)}
		</Item>
	)
}
